import { useRef, useCallback } from 'react'
import { Upload, X, Film, Link2 } from 'lucide-react'
import { toast } from 'sonner'

// LINE limits (mirror line_message_builder.py on the backend)
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB
export const MAX_VIDEO_SIZE = 200 * 1024 * 1024 // 200MB
const IMAGE_TYPES = ['image/jpeg', 'image/png']
const VIDEO_TYPES = ['video/mp4']

export interface RichState {
  imageFile: File | null
  imageUrl: string | null
  link: string
  label: string
  title: string
}

export interface VideoState {
  videoFile: File | null
  posterFile: File | null
  posterUrl: string | null
}

function pickFile(
  file: File,
  types: string[],
  maxBytes: number,
  label: string,
  onOk: (f: File) => void,
) {
  if (!types.includes(file.type)) {
    toast.error(`${label} must be ${types.map((t) => t.split('/')[1].toUpperCase()).join(' or ')} format.`)
    return
  }
  if (file.size > maxBytes) {
    toast.error(`${label} must be less than ${Math.round(maxBytes / (1024 * 1024))}MB.`)
    return
  }
  onOk(file)
}

// --- Rich (Flex) message authoring ---
export function RichEditor({
  rich,
  setRich,
}: {
  rich: RichState
  setRich: (patch: Partial<RichState>) => void
}) {
  const imgRef = useRef<HTMLInputElement>(null)
  const onImage = useCallback(
    (file: File) =>
      pickFile(file, IMAGE_TYPES, MAX_IMAGE_SIZE, 'Image', (f) => {
        if (rich.imageUrl) URL.revokeObjectURL(rich.imageUrl)
        setRich({ imageFile: f, imageUrl: URL.createObjectURL(f) })
      }),
    [rich.imageUrl, setRich],
  )
  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="text-xs font-medium text-black/60 mb-1.5">Image</div>
        {rich.imageUrl ? (
          <div className="relative">
            <img src={rich.imageUrl} alt="Rich" className="w-full rounded object-cover max-h-64" />
            <button
              type="button"
              onClick={() => {
                if (rich.imageUrl) URL.revokeObjectURL(rich.imageUrl)
                setRich({ imageFile: null, imageUrl: null })
              }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-black/15 rounded p-8 text-center cursor-pointer hover:border-black/25"
            onClick={() => imgRef.current?.click()}
          >
            <Upload className="w-8 h-8 text-black/30 mx-auto" />
            <p className="text-sm text-black/50 mt-2">Click to upload a hero image</p>
            <p className="text-xs text-black/40 mt-1">JPEG or PNG, max 10MB — tappable, opens your link</p>
          </div>
        )}
        <input
          ref={imgRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) onImage(f)
            if (e.target) e.target.value = ''
          }}
        />
      </div>

      <div>
        <div className="text-xs font-medium text-black/60 mb-1.5">Link (opens when tapped)</div>
        <div className="flex items-center gap-2 rounded border border-black/15 px-2.5 focus-within:ring-2 focus-within:ring-[#06C755]/40">
          <Link2 className="w-4 h-4 text-black/30 shrink-0" />
          <input
            type="url"
            value={rich.link}
            onChange={(e) => setRich({ link: e.target.value })}
            placeholder="https://your-store.com/promo"
            className="flex-1 py-2 text-sm focus:outline-none"
          />
        </div>
        {rich.link.trim() !== '' && !/^https:\/\//.test(rich.link.trim()) && (
          <p className="text-xs text-red-500 mt-1">Link must start with https://</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-medium text-black/60 mb-1.5">Button label</div>
          <input
            type="text"
            value={rich.label}
            maxLength={40}
            onChange={(e) => setRich({ label: e.target.value })}
            placeholder="View"
            className="w-full rounded border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06C755]/40"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-black/60 mb-1.5">Title (optional)</div>
          <input
            type="text"
            value={rich.title}
            maxLength={100}
            onChange={(e) => setRich({ title: e.target.value })}
            placeholder="Big sale this week"
            className="w-full rounded border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06C755]/40"
          />
        </div>
      </div>
    </div>
  )
}

// --- Video message authoring ---
export function VideoEditor({
  video,
  setVideo,
}: {
  video: VideoState
  setVideo: (patch: Partial<VideoState>) => void
}) {
  const vidRef = useRef<HTMLInputElement>(null)
  const posterRef = useRef<HTMLInputElement>(null)
  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="text-xs font-medium text-black/60 mb-1.5">Video</div>
        {video.videoFile ? (
          <div className="flex items-center gap-3 rounded border border-black/10 bg-gray-50 p-3">
            <Film className="w-5 h-5 text-[#06C755] shrink-0" />
            <span className="text-sm text-[#353a40] truncate flex-1">{video.videoFile.name}</span>
            <span className="text-xs text-black/45 shrink-0">
              {(video.videoFile.size / (1024 * 1024)).toFixed(1)}MB
            </span>
            <button
              type="button"
              onClick={() => setVideo({ videoFile: null })}
              className="text-black/40 hover:text-red-500 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-black/15 rounded p-8 text-center cursor-pointer hover:border-black/25"
            onClick={() => vidRef.current?.click()}
          >
            <Film className="w-8 h-8 text-black/30 mx-auto" />
            <p className="text-sm text-black/50 mt-2">Click to upload a video</p>
            <p className="text-xs text-black/40 mt-1">MP4 only, max 200MB</p>
          </div>
        )}
        <input
          ref={vidRef}
          type="file"
          accept="video/mp4"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) pickFile(f, VIDEO_TYPES, MAX_VIDEO_SIZE, 'Video', (file) => setVideo({ videoFile: file }))
            if (e.target) e.target.value = ''
          }}
        />
      </div>

      <div>
        <div className="text-xs font-medium text-black/60 mb-1.5">Preview image (thumbnail)</div>
        {video.posterUrl ? (
          <div className="relative">
            <img src={video.posterUrl} alt="Preview" className="w-full rounded object-cover max-h-64" />
            <button
              type="button"
              onClick={() => {
                if (video.posterUrl) URL.revokeObjectURL(video.posterUrl)
                setVideo({ posterFile: null, posterUrl: null })
              }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-black/15 rounded p-6 text-center cursor-pointer hover:border-black/25"
            onClick={() => posterRef.current?.click()}
          >
            <Upload className="w-7 h-7 text-black/30 mx-auto" />
            <p className="text-sm text-black/50 mt-2">Click to upload a preview image</p>
            <p className="text-xs text-black/40 mt-1">JPEG or PNG, max 10MB — shown before the video plays</p>
          </div>
        )}
        <input
          ref={posterRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f)
              pickFile(f, IMAGE_TYPES, MAX_IMAGE_SIZE, 'Preview image', (file) => {
                if (video.posterUrl) URL.revokeObjectURL(video.posterUrl)
                setVideo({ posterFile: file, posterUrl: URL.createObjectURL(file) })
              })
            if (e.target) e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
